import isEqual from 'lodash/isEqual';
import memoizeOne from 'memoize-one';
import { OPTIMISTIC_ID_PREFIX } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { isItemSkeleton } from '../../common/utils/row-placeholder';
import type { ListViewIssue } from '../../services/get-list-view-issues/types';
import type { ExpandedItems, ChildrenItems } from '../hierarchy/hierarchy-store';
import type { ListViewControllerData } from '../list-view-issues/types';

export const getFlattenIssuesList = ({
	issues,
	expandedItems,
}: {
	expandedItems: ExpandedItems;
	issues?: ListViewControllerData[];
}) => {
	if (!issues || issues.length === 0) {
		return [];
	}
	const flattenedIssues: ListViewControllerData[] = issues.flatMap((issue) => {
		const isExpanded = expandedItems[toIssueId(issue.issueId)];
		const children = isExpanded
			? getFlattenIssuesList({ issues: issue.children, expandedItems })
			: [];
		return [issue, ...children];
	});
	return flattenedIssues;
};

// fnc to get all the children of an issue even the grandchildren
export const getAllIssueChildren = memoizeOne(
	({ issue, childrenItems }: { issue?: ListViewIssue; childrenItems: ChildrenItems }) => {
		if (!issue) return [];

		const issueChildren = childrenItems[issue.issueId];
		if (!issueChildren) return [];

		let issuesIds: string[] = [];
		issueChildren.data?.forEach((child) => {
			issuesIds = [
				...issuesIds,
				String(child.issueId),
				...getAllIssueChildren({ issue: child, childrenItems }),
			];
		});

		return issuesIds;
	},
	isEqual,
);

export const getAllIssuesIds = ({
	data,
	childrenItems,
}: {
	data: ListViewControllerData[];
	childrenItems: ChildrenItems;
}) => {
	let issuesIds: string[] = [];
	data.forEach((issue) => {
		const { issueId, id } = issue;
		if (!isGroupItem(issueId) && !isItemSkeleton(issueId) && !id.startsWith(OPTIMISTIC_ID_PREFIX)) {
			issuesIds.push(String(issueId));
		}
		if (childrenItems[issueId]) {
			issuesIds = [...issuesIds, ...getAllIssueChildren({ issue, childrenItems })];
		}
	});
	return issuesIds;
};
